import React, { useEffect, useMemo } from 'react';
import { Content, Header, List, Title, Wrapper } from './list-own-products.styles';
import { Typography } from '@shared/ui/typography/typography';
import { ArrowsChevronRight } from '@vtbhome/juicyfront/indexIcon';
import { COLORS } from '@shared/config/colors/colors';
import { useUserProductsListSharedQuery } from '@entities/product/libs/hooks/use-user-products-list-shared-query/use-user-products-list-shared-query';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { ROUTE_ADMIN_CATALOG_COMPLETED } from '@shared/config/routes/routes';
import { CardOwnOrder } from '@entities/order/ui/card-own-order/card-own-order';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { usePrefetchProduct } from '@entities/product/api/product-api';
import { usePrefetchOrder } from '@entities/order/api/order-api';
import { listOrdersInProductParameters } from '@shared/lib/utils/list-orders-in-product-parameters/list-orders-in-product-parameters';
import { DEFAULT_USER_COMPLETED_PRODUCTS_LIST_PARAMETERS } from '@entities/product/config/store-constants';
import { UserProductsListDataRequest } from '@entities/product/api/product-types';
import { LinkScroll } from '@shared/ui/link-scroll/link-scroll';

export const ListOwnProducts: React.FC = () => {
    const user = useCurrentUserSelector();

    const parameters: UserProductsListDataRequest = useMemo(() => {
        return {
            ...DEFAULT_USER_COMPLETED_PRODUCTS_LIST_PARAMETERS,
            userId: user?.id || 0,
        };
    }, [user]);

    const [getOrdersList, getOrdersListResult] = useUserProductsListSharedQuery(parameters);

    const restrictedList = useMemo(() => {
        return getOrdersListResult.data?.items;
    }, [getOrdersListResult.data]);

    const ordersData = getOrdersListResult.data;
    const ordersListLength = restrictedList?.length || 0;
    const error = getQueryError(getOrdersListResult);

    const prefetchProduct = usePrefetchProduct('productShortInfo', { ifOlderThan: 300 });
    const prefetchOrder = usePrefetchOrder('ordersList', { ifOlderThan: 300 });

    const handlePrefetch = (productId: number) => () => {
        prefetchProduct(productId);
        prefetchOrder(
            listOrdersInProductParameters({
                productId,
                user,
            })
        );
    };

    useEffect(() => {
        getOrdersList(parameters);
    }, [parameters]);

    return (
        <Wrapper>
            <Header>
                <Title>
                    <Typography size='h6'>Мои продукты</Typography>
                </Title>
                <LinkScroll to={ROUTE_ADMIN_CATALOG_COMPLETED}>
                    <ArrowsChevronRight color={COLORS.neutral_50} />
                </LinkScroll>
            </Header>
            <Content>
                <NetworkStateNew
                    isLoading={
                        getOrdersListResult.isLoading ||
                        getOrdersListResult.isFetching ||
                        getOrdersListResult.isUninitialized
                    }
                    isInitialized={Boolean(ordersData)}
                    isError={Boolean(error)}
                    hasData={Boolean(ordersListLength)}
                    isFiltered={Boolean(getOrdersListResult.data?.search)}
                    errorProps={{
                        title: 'Ошибка загрузки продуктов',
                        description: error?.message,
                    }}
                    emptyProps={{
                        title: 'Продукты отсутствуют',
                    }}
                    emptyFilteredProps={{
                        title: 'Продукты не найдены',
                    }}
                    initialLoaderProps={{
                        style: { height: 322 },
                    }}>
                    <List>
                        {restrictedList?.map((order, idx) => (
                            <CardOwnOrder
                                key={order.id}
                                hasBorder={idx !== ordersListLength - 1}
                                product={order}
                                onPrefetchProduct={handlePrefetch(order.id)}
                            />
                        ))}
                    </List>
                </NetworkStateNew>
            </Content>
        </Wrapper>
    );
};
